import { useCallback, useState } from 'react';

/**
 * Shared "refresh this provider feed" action (openspec §10).
 *
 * Delegates to each source's existing `retry(source)` revalidation path
 * instead of inventing new backend plumbing. `retry` only reloads the
 * given source's items — it never touches `expandedItemIds`, scroll
 * position, filters, or drafts — so local UI state is preserved for free.
 *
 * An optional `beforeRetry` hook lets a provider run its own pre-fetch step
 * (e.g. Slack's `slackClient.refresh(hours)`, which asks the live Slack API
 * to pull recent history before the shared `retry('slack')` reload) without
 * duplicating the busy/error state management every view previously wrote
 * by hand.
 */
export function useProviderRefresh(
  source: string,
  retry: (source?: string) => Promise<void>,
  beforeRetry?: () => Promise<void>,
) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      if (beforeRetry) await beforeRetry();
      await retry(source);
    } catch (err) {
      setError(err instanceof Error ? err.message : `No se pudo sincronizar ${source}.`);
    } finally {
      setIsRefreshing(false);
    }
  }, [source, retry, beforeRetry]);

  return { isRefreshing, error, refresh };
}
